/** Wire payload → one view tree, plus the flattening that makes it cheap to render.
 *
 *  ## Why flatten
 *
 *  The largest body in the corpus is 329 KB, which is thousands of AST nodes.
 *  Rather than add a windowing dependency, the tree is collapsed by default and
 *  only the *visible* nodes are flattened into an array — collapsing IS the
 *  virtualization, so rendered rows stay in the low hundreds no matter how big
 *  the source is.
 *
 *  ## Collapse defaults
 *
 *  - Composition nodes (notification, delivery, layout, blocks) open.
 *  - AST nodes open to `AST_AUTO_DEPTH`, then closed.
 *  - Anything with more than `BIG_SUBTREE` descendants stays closed regardless,
 *    so one 32-way switch cannot bury the rest of the template.
 */
import type {
  AnatomyBlock,
  AnatomyEditTarget,
  AnatomyNode,
  NotificationAnatomy,
  TemplateAnatomy,
} from '@/api/notificationAnatomy';

import type { AnatomyViewNode, FlatRow, FlattenOptions, ViewNodeKind } from './types';

/** AST levels opened automatically below an `ast` wrapper. */
export const AST_AUTO_DEPTH = 2;
/** A subtree this large stays closed even inside the auto-open depth. */
export const BIG_SUBTREE = 200;

const COMPOSITION_KINDS: ReadonlySet<ViewNodeKind> = new Set<ViewNodeKind>([
  'notification',
  'delivery',
  'layout',
  'standard-template',
  'layout-block',
  'subject',
  'blocks',
  'block',
  'advanced',
  'advanced-rule',
  'ast',
]);

function descendantsOf(children: AnatomyViewNode[]): number {
  return children.reduce((sum, c) => sum + 1 + c.descendants, 0);
}

function node(
  id: string,
  kind: ViewNodeKind,
  label: string,
  children: AnatomyViewNode[] = [],
  extra: Partial<AnatomyViewNode> = {},
): AnatomyViewNode {
  return {
    id,
    kind,
    label,
    badges: [],
    children,
    descendants: descendantsOf(children),
    ...extra,
  };
}

// ---------------------------------------------------------------------------
// AST
// ---------------------------------------------------------------------------

/** Inherited by every node in one AST: where its lines can be edited, and
 *  which block (if any) owns the content. */
interface AstContext {
  editTarget?: AnatomyEditTarget;
  withinBlock?: string;
}

/** One wire AST node → one view node. `descendants` is already computed
 *  server-side, so this is a straight relabel — no second traversal cost. */
function fromAstNode(raw: AnatomyNode, idPrefix: string, ctx: AstContext): AnatomyViewNode {
  const children = (raw.children ?? []).map((c) => fromAstNode(c, idPrefix, ctx));
  return {
    id: `${idPrefix}:${raw.id}`,
    kind: raw.kind,
    label: raw.label,
    line: raw.line ?? null,
    badges: raw.badges ?? [],
    children,
    descendants: raw.descendants ?? descendantsOf(children),
    blank: raw.blank,
    textLength: raw.length,
    conditionRaw: raw.condition_raw,
    condition: raw.condition,
    verdict: raw.verdict,
    ...ctx,
  };
}

/** A `TemplateAnatomy` → an `ast` wrapper node.
 *
 *  The wrapper exists so an unparsed source (Jinja2, over the size cap) still
 *  gets a row that says WHY there is no tree, instead of rendering as empty. */
export function buildTemplateNode(
  anatomy: TemplateAnatomy | null | undefined,
  id: string,
  label: string,
  /** Set when this template IS a block definition, so every node beneath it can
   *  be highlighted as content the layout does not own. */
  withinBlock?: string,
): AnatomyViewNode {
  if (!anatomy) {
    return node(id, 'ast', label, [], { detail: 'no template' });
  }
  const ctx: AstContext = {
    ...(anatomy.edit_target ? { editTarget: anatomy.edit_target } : {}),
    ...(withinBlock ? { withinBlock } : {}),
  };
  if (!anatomy.parsed || !anatomy.root) {
    const reason =
      anatomy.reason === 'jinja2'
        ? 'Jinja2 — structure not parsed'
        : anatomy.reason === 'too_large'
          ? 'over the size cap — structure not parsed'
          : 'structure not parsed';
    return node(id, 'ast', label, [], {
      detail: reason,
      unparsed: anatomy.reason
        ? { reason: anatomy.reason, bytes: anatomy.bytes }
        : undefined,
      errors: anatomy.errors,
      ...ctx,
    });
  }
  const root = fromAstNode(anatomy.root, id, ctx);
  return node(id, 'ast', label, root.children, {
    detail: `${anatomy.bytes.toLocaleString()} bytes`,
    errors: anatomy.errors,
    ...ctx,
  });
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/** The alias is what a template writes, so it is what the row says; the
 *  underlying codename only appears in the detail line when they differ. */
function blockLabel(block: AnatomyBlock): string {
  return `block.${block.alias}`;
}

function blockDetail(block: AnatomyBlock): string {
  const bits: string[] = [block.type];
  if (block.alias !== block.codename) bits.push(`codename ${block.codename}`);
  if (block.source === 'list') bits.push('from a list');
  if (block.error_mode === 'hard') bits.push('hard error mode');
  if (!block.enabled) bits.push('disabled');
  return bits.join(' · ');
}

function buildBlockNode(
  block: AnatomyBlock,
  index: number,
  gates: NotificationAnatomy['feature_gates'],
): AnatomyViewNode {
  const id = `block.${index}`;
  const children: AnatomyViewNode[] = [];
  if (block.anatomy) {
    children.push(
      buildTemplateNode(block.anatomy, `${id}.ast`, 'definition', `block.${block.alias}`),
    );
  }

  // `[]` = no gate; a feature with no rows anywhere still yields six levels, so
  // the distinction between "not gated" and "gated, nothing configured" holds.
  const gateLevels = block.feature ? (gates[block.feature] ?? []) : [];

  const n = node(id, 'block', blockLabel(block), children, {
    detail: blockDetail(block),
    block,
    gateLevels,
  });
  if (block.feature) {
    // `ref` carries the codename so the badge can offer the flag rows that
    // gate it (the label is display text and must not be parsed for it).
    n.badges = [
      ...n.badges,
      { code: 'feature', label: `feature: ${block.feature}`, ref: block.feature },
    ];
  }
  if (!block.enabled) {
    n.badges = [...n.badges, { code: 'disabled', label: 'disabled' }];
  }
  return n;
}

function buildLayoutNode(anatomy: NotificationAnatomy): AnatomyViewNode | null {
  const { layout, channel } = anatomy;
  if (!layout) return null;

  if (layout.custom) {
    return node(
      'layout',
      'layout',
      'Layout — custom template',
      [buildTemplateNode(layout.anatomy, 'layout.ast', `${channel ?? 'channel'} body`)],
      { detail: "the delivery method's own body" },
    );
  }

  const st = layout.standard_template;
  const blocks = layout.blocks.map((b, i) =>
    node(
      `layout.block.${i}`,
      'layout-block',
      b.codename,
      [buildTemplateNode(b.anatomy, `layout.block.${i}.ast`, b.name || b.codename)],
      // The block id was on the wire and dropped here, so the row could name a
      // codename but not open it. Keep it.
      { detail: `sequence ${b.sequence}`, layoutBlockId: b.id },
    ),
  );
  return node(
    'layout',
    'layout',
    st ? `Layout — standard template “${st.name}” v${st.version}` : 'Layout — standard template',
    blocks,
    { detail: st ? `#${st.id} · ${st.template_language}` : 'not set' },
  );
}

function buildAdvancedNode(anatomy: NotificationAnatomy): AnatomyViewNode | null {
  const adv = anatomy.advanced_rules;
  if (!adv.rules.length) return null;

  const rules = adv.rules.map((rule, i) =>
    node(
      `advanced.${i}`,
      'advanced-rule',
      `if ${rule.condition_any_all_label.toLowerCase()} trigger is ${rule.condition_label}`,
      [],
      {
        detail:
          rule.send_after_min != null
            ? `after ${rule.send_after_min} min`
            : 'immediately',
        rule,
        methods: anatomy.delivery_methods,
      },
    ),
  );
  return node(
    'advanced',
    'advanced',
    `Advanced delivery rules (${adv.rules.length})`,
    rules,
    {
      // Rows are preserved when the ordering changes, so saying only "N rules"
      // would claim an escalation that never runs.
      detail: adv.active
        ? adv.ordering_label
        : `${adv.ordering_label} — these rules are stored but INACTIVE`,
      advanced: adv,
      // The drawer draws every rule from this one node, so it needs the method
      // labels here too — not only on the per-rule children.
      methods: anatomy.delivery_methods,
    },
  );
}

/** The whole notification as one tree. */
export function buildNotificationTree(anatomy: NotificationAnatomy): AnatomyViewNode {
  const children: AnatomyViewNode[] = [];

  const dmChildren: AnatomyViewNode[] = [];
  if (anatomy.subject) {
    dmChildren.push(buildTemplateNode(anatomy.subject, 'subject', 'Subject'));
  }
  const layout = buildLayoutNode(anatomy);
  if (layout) dmChildren.push(layout);

  const active = anatomy.delivery_methods.find((d) => d.channel === anatomy.channel);
  children.push(
    node(
      'delivery',
      'delivery',
      active
        ? `${active.method_label} — ${anatomy.format}`
        : `No composition-aware delivery method`,
      dmChildren,
      {
        detail: active
          ? `#${active.id}${active.enabled ? '' : ' · disabled'}`
          : 'nothing to parse for this notification',
      },
    ),
  );

  if (anatomy.blocks.length) {
    children.push(
      node(
        'blocks',
        'blocks',
        `Blocks (${anatomy.blocks.length})`,
        anatomy.blocks.map((b, i) => buildBlockNode(b, i, anatomy.feature_gates)),
        {
          // The order IS the evaluation order, and it is why a later default
          // block can reference an earlier explicit one.
          detail: anatomy.notification.disable_default_blocks
            ? 'evaluation order · default lists disabled'
            : 'evaluation order · includes default lists',
        },
      ),
    );
  }

  const advanced = buildAdvancedNode(anatomy);
  if (advanced) children.push(advanced);

  const nf = anatomy.notification;
  return node(
    'notification',
    'notification',
    nf.name || `Notification #${nf.id}`,
    children,
    {
      detail: [
        nf.event,
        nf.tour_operator ? nf.tour_operator.code : 'system',
        nf.enabled ? null : 'disabled',
        nf.language_policy ? `policy ${nf.language_policy.codename}` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    },
  );
}

// ---------------------------------------------------------------------------
// Expansion + flattening
// ---------------------------------------------------------------------------

/** Ids open on first paint. Composition open, AST shallow, big subtrees shut. */
export function defaultExpanded(root: AnatomyViewNode): Set<string> {
  const open = new Set<string>();

  const walk = (n: AnatomyViewNode, astDepth: number | null) => {
    if (!n.children.length) return;

    const isComposition = COMPOSITION_KINDS.has(n.kind);
    // `ast` is a composition node whose CHILDREN start the AST depth count.
    const nextAstDepth = n.kind === 'ast' ? 0 : astDepth === null ? null : astDepth + 1;

    const withinAutoDepth = astDepth === null || astDepth < AST_AUTO_DEPTH;
    const small = n.descendants <= BIG_SUBTREE;
    if ((isComposition || withinAutoDepth) && small) open.add(n.id);
    // A composition node is structural scaffolding: open it even when huge, or
    // a monolith would hide its own layout row.
    else if (isComposition) open.add(n.id);

    for (const child of n.children) walk(child, nextAstDepth);
  };

  walk(root, null);
  return open;
}

/** Visible rows, in render order. Only expanded subtrees are walked. */
export function flattenVisible(
  root: AnatomyViewNode,
  expanded: ReadonlySet<string>,
  options: FlattenOptions = {},
): FlatRow[] {
  const rows: FlatRow[] = [];
  const showBlank = options.showBlank ?? false;

  const walk = (n: AnatomyViewNode, depth: number) => {
    if (!showBlank && n.blank) return;
    const hasChildren = n.children.length > 0;
    const isOpen = hasChildren && expanded.has(n.id);
    rows.push({ node: n, depth, hasChildren, expanded: isOpen });
    if (!isOpen) return;
    for (const child of n.children) walk(child, depth + 1);
  };

  walk(root, 0);
  return rows;
}

/** Every id on the path to `id`, so a deep link can open just that spine. */
export function pathTo(root: AnatomyViewNode, id: string): string[] {
  const found: string[] = [];
  const walk = (n: AnatomyViewNode, trail: string[]): boolean => {
    const next = [...trail, n.id];
    if (n.id === id) {
      found.push(...next);
      return true;
    }
    return n.children.some((c) => walk(c, next));
  };
  walk(root, []);
  return found;
}

/** Ids of every node with children — the "expand all" target. */
export function allExpandableIds(root: AnatomyViewNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: AnatomyViewNode) => {
    if (n.children.length) ids.add(n.id);
    n.children.forEach(walk);
  };
  walk(root);
  return ids;
}
